'use client';

import EventEmitter from 'eventemitter3';
import { useTranslations } from 'next-intl';
import { useEffect, useState, type JSX, type ReactNode } from 'react';

import { Button } from '~/components/button';
import {
  Dialog,
  DialogTitle,
  DialogFooter,
  DialogHeader,
  DialogContent,
  DialogDescription,
} from '~/components/dialog';

interface IOnSubmitParams {
  close: () => void;
}

interface IConfirmProps {
  title?: string;
  description?: ReactNode;
  getLoading?: () => boolean;
  onSubmit?: (params: IOnSubmitParams) => void;
}

const defaultProps: IConfirmProps = {
  title: 'Are you sure?',
};

export function ConfirmModal(): JSX.Element {
  const t = useTranslations();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [props, setProps] = useState<IConfirmProps>(defaultProps);

  useEffect(() => {
    function openConfirm(params: IConfirmProps = {}) {
      setIsOpen(true);
      setProps({
        ...defaultProps,
        ...params,
      });
    }

    confirmModalEvent.on('confirm', openConfirm);

    return () => {
      confirmModalEvent.removeListener('confirm', openConfirm);
    };
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    setIsLoading(false);
    setProps(defaultProps);
  };

  const { title, getLoading, onSubmit, description } = props;

  const submit = () => {
    onSubmit?.({
      close: handleClose,
    });

    setIsLoading(getLoading?.() ?? false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(state) => setIsOpen(state)}>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button disabled={isLoading} variant='outline' onClick={handleClose}>
            {t('commons.cancel')}
          </Button>
          <Button disabled={isLoading} variant='destructive' onClick={submit}>
            {t('commons.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface IConfirmEventProps {
  confirm: (params?: IConfirmProps) => void;
}

export const confirmModalEvent = new EventEmitter<IConfirmEventProps>();
